import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { api, formatApiErrorDetail } from "../../lib/api";
import { fmtCompact, fmtMulti, curSymbol } from "../../lib/money";
import { toast } from "sonner";
import {
  Pencil,
  Trash2,
  HardHat,
  Users,
  Phone,
  Mail,
  Wallet,
  UserPlus,
  X,
  Search,
  GripVertical,
  CornerDownRight,
  CornerUpLeft,
} from "lucide-react";

const STATUS_DOT = {
  green: "bg-emerald-500",
  yellow: "bg-amber-400",
  red: "bg-red-500",
  gray: "bg-zinc-300",
};

const EMPTY_STAFF = { role: "", name: "", phone: "", email: "" };

// Flat staff array (parent_id ile) → derinlik bilgili, ağaç sıralı düz liste.
function buildFlat(staff) {
  const ids = new Set((staff || []).map((s) => s.id));
  const byParent = {};
  for (const s of staff || []) {
    const p = s.parent_id && ids.has(s.parent_id) ? s.parent_id : "__root__";
    (byParent[p] = byParent[p] || []).push(s);
  }
  const out = [];
  const walk = (pid, depth) => {
    for (const s of byParent[pid] || []) {
      out.push({ ...s, depth });
      walk(s.id, depth + 1);
    }
  };
  walk("__root__", 0);
  return out;
}

function descIds(flat, rootId) {
  const ids = new Set([rootId]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const s of flat) {
      if (s.parent_id && ids.has(s.parent_id) && !ids.has(s.id)) {
        ids.add(s.id);
        changed = true;
      }
    }
  }
  ids.delete(rootId);
  return ids;
}

function subtreeEnd(arr, idx) {
  const ids = descIds(arr, arr[idx].id);
  let end = idx;
  while (end + 1 < arr.length && ids.has(arr[end + 1].id)) end++;
  return end;
}

const trLower = (s) => (s || "").toLocaleLowerCase("tr");

export default function HubDetailSheet({ hubId, open, onClose, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [staffForm, setStaffForm] = useState(EMPTY_STAFF);
  const [editingStaffId, setEditingStaffId] = useState(null);
  const [savingStaff, setSavingStaff] = useState(false);
  const [confirmStaffDelete, setConfirmStaffDelete] = useState(null);
  const [standQ, setStandQ] = useState("");
  const [staffQ, setStaffQ] = useState("");
  const [dragId, setDragId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null); // {id, zone}
  const invalidRef = useRef(new Set());

  const fetchDetail = async () => {
    try {
      const { data } = await api.get(`/hubs/${hubId}/detail`);
      setDetail(data);
    } catch {
      toast.error("Hub detayı yüklenemedi");
    }
  };

  useEffect(() => {
    if (!open || !hubId) return;
    setLoading(true);
    setStaffForm(EMPTY_STAFF);
    setEditingStaffId(null);
    setConfirmStaffDelete(null);
    setStandQ("");
    setStaffQ("");
    fetchDetail().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hubId]);

  const stands = detail?.stands || [];
  const filteredStands = useMemo(() => {
    const q = trLower(standQ.trim());
    if (!q) return stands;
    return stands.filter((s) =>
      trLower(
        [s.company_name, s.stand_no, s.country, s.stage, s.stand_type, s.contact_name].join(" "),
      ).includes(q),
    );
  }, [stands, standQ]);

  const flatStaff = useMemo(() => buildFlat(detail?.staff || []), [detail?.staff]);
  const staffQuery = trLower(staffQ.trim());
  const visibleStaff = useMemo(() => {
    if (!staffQuery) return flatStaff;
    return flatStaff.filter((s) =>
      trLower([s.name, s.role, s.phone, s.email].join(" ")).includes(staffQuery),
    );
  }, [flatStaff, staffQuery]);
  const dndEnabled = !staffQuery;

  const submitStaff = async (e) => {
    e.preventDefault();
    if (!staffForm.name.trim()) {
      toast.error("Ad Soyad zorunludur");
      return;
    }
    setSavingStaff(true);
    try {
      if (editingStaffId) {
        await api.patch(`/hubs/${hubId}/staff/${editingStaffId}`, staffForm);
        toast.success("Görevli güncellendi");
      } else {
        await api.post(`/hubs/${hubId}/staff`, staffForm);
        toast.success("Görevli eklendi");
      }
      setStaffForm(EMPTY_STAFF);
      setEditingStaffId(null);
      await fetchDetail();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingStaff(false);
    }
  };

  const editStaff = (s) => {
    setEditingStaffId(s.id);
    setStaffForm({
      role: s.role || "",
      name: s.name || "",
      phone: s.phone || "",
      email: s.email || "",
    });
  };

  const removeStaff = async (id) => {
    try {
      await api.delete(`/hubs/${hubId}/staff/${id}`);
      toast.success("Görevli silindi");
      setConfirmStaffDelete(null);
      if (editingStaffId === id) {
        setEditingStaffId(null);
        setStaffForm(EMPTY_STAFF);
      }
      await fetchDetail();
      onChanged?.();
    } catch {
      toast.error("Görevli silinemedi");
    }
  };

  const clearDrag = () => {
    setDragId(null);
    setDropTarget(null);
    invalidRef.current = new Set();
  };

  const applyMove = async (draggedId, targetId, zone) => {
    const flat = buildFlat(detail?.staff || []);
    const subtreeSet = new Set([draggedId, ...descIds(flat, draggedId)]);
    if (targetId !== "__root__" && subtreeSet.has(targetId)) return;
    const subtree = flat.filter((x) => subtreeSet.has(x.id));
    const rest = flat.filter((x) => !subtreeSet.has(x.id));
    let newParent = null;
    let insertIdx = rest.length;
    if (zone !== "root") {
      const tIdx = rest.findIndex((x) => x.id === targetId);
      if (tIdx === -1) return;
      const target = rest[tIdx];
      if (zone === "child") {
        newParent = target.id;
        insertIdx = subtreeEnd(rest, tIdx) + 1;
      } else if (zone === "before") {
        newParent = target.parent_id || null;
        insertIdx = tIdx;
      } else {
        newParent = target.parent_id || null;
        insertIdx = subtreeEnd(rest, tIdx) + 1;
      }
    }
    const moved = subtree.map((x, i) => (i === 0 ? { ...x, parent_id: newParent } : x));
    const newFlat = [...rest.slice(0, insertIdx), ...moved, ...rest.slice(insertIdx)];
    const cleaned = newFlat.map(({ depth, ...s }) => s);
    setDetail((d) => ({ ...d, staff: cleaned }));
    try {
      await api.put(`/hubs/${hubId}/staff-order`, {
        order: cleaned.map((s) => ({ id: s.id, parent_id: s.parent_id || null })),
      });
    } catch {
      toast.error("Taşıma kaydedilemedi");
      fetchDetail();
    }
  };

  const zoneFor = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - r.top;
    if (y < r.height * 0.35) return "before";
    if (y > r.height * 0.65) return "after";
    return "child";
  };

  const promote = (s) => {
    const parent = flatStaff.find((x) => x.id === s.parent_id);
    if (parent) applyMove(s.id, parent.id, "after");
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        className="w-full overflow-y-auto sm:max-w-xl"
        data-testid="hub-detail-sheet"
      >
        {loading || !detail ? (
          <div className="py-16 text-center text-sm text-zinc-400">Yükleniyor…</div>
        ) : (
          <div className="space-y-6">
            <SheetHeader>
              <div className="flex items-center gap-3">
                <span
                  className="h-4 w-4 shrink-0 rounded-full"
                  style={{ backgroundColor: detail.color }}
                />
                <SheetTitle className="text-xl" data-testid="hub-detail-title">
                  {detail.name}
                </SheetTitle>
              </div>
            </SheetHeader>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                  <HardHat className="h-3.5 w-3.5" /> Stand
                </div>
                <div
                  className="mt-1 text-2xl font-semibold text-zinc-900"
                  data-testid="hub-detail-stand-count"
                >
                  {detail.stand_count}
                </div>
              </div>
              <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                  <Wallet className="h-3.5 w-3.5" /> Toplam Bütçe
                </div>
                <div
                  className="mt-1 text-2xl font-semibold text-zinc-900"
                  data-testid="hub-detail-budget"
                >
                  {fmtMulti(detail.budget_totals)}
                </div>
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="text-sm font-semibold text-zinc-900">Stantlar</div>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
                  <Input
                    value={standQ}
                    onChange={(e) => setStandQ(e.target.value)}
                    placeholder="Stant ara…"
                    className="h-8 w-44 pl-8 text-sm"
                    data-testid="hub-stand-search-input"
                  />
                </div>
              </div>
              {filteredStands.length === 0 ? (
                <div className="rounded-lg border border-dashed border-zinc-300 py-8 text-center text-sm text-zinc-400">
                  {standQ
                    ? "Aramayla eşleşen stant yok."
                    : "Bu hub'a bağlı İnşaat Takip kaydı yok."}
                </div>
              ) : (
                <div
                  className="divide-y divide-zinc-100 rounded-lg border border-zinc-200"
                  data-testid="hub-detail-stands"
                >
                  {filteredStands.map((s) => (
                    <div
                      key={s.id}
                      className="flex items-center gap-3 px-3 py-2.5"
                      data-testid={`hub-stand-row-${s.id}`}
                    >
                      <span
                        className={`h-2 w-2 shrink-0 rounded-full ${STATUS_DOT[s.status] || STATUS_DOT.gray}`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-zinc-900">
                          {s.company_name || "—"}
                        </div>
                        <div className="truncate text-xs text-zinc-500">
                          {[s.stand_type, s.stage, s.country]
                            .filter(Boolean)
                            .join(" · ") || "—"}
                        </div>
                      </div>
                      {s.stand_no && (
                        <span className="rounded-md bg-zinc-100 px-2 py-0.5 text-xs font-semibold text-zinc-700">
                          #{s.stand_no}
                        </span>
                      )}
                      <span className="w-20 text-right text-sm font-medium text-zinc-700">
                        {s.stand_cost
                          ? fmtCompact(s.stand_cost, curSymbol(s.currency))
                          : "—"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 text-sm font-semibold text-zinc-900">
                  <Users className="h-4 w-4" /> Görevliler
                  <span className="text-xs font-normal text-zinc-400">
                    ({flatStaff.length})
                  </span>
                </div>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
                  <Input
                    value={staffQ}
                    onChange={(e) => setStaffQ(e.target.value)}
                    placeholder="Kişi ara…"
                    className="h-8 w-44 pl-8 text-sm"
                    data-testid="hub-staff-search-input"
                  />
                </div>
              </div>
              {dndEnabled && flatStaff.length > 1 && (
                <p className="mb-2 text-[11px] text-zinc-400">
                  Üst/alt kenara bırakın → <b>aynı seviyeye</b> taşınır ·
                  Ortasına bırakın → <b>alt görevli</b> olur.
                </p>
              )}

              {visibleStaff.length === 0 && staffQuery ? (
                <div className="mb-3 rounded-lg border border-dashed border-zinc-300 py-6 text-center text-sm text-zinc-400">
                  Aramayla eşleşen kişi yok.
                </div>
              ) : visibleStaff.length > 0 ? (
                <div className="mb-3 divide-y divide-zinc-100 rounded-lg border border-zinc-200">
                  {visibleStaff.map((s) => {
                    const isTarget = dropTarget?.id === s.id;
                    const zone = isTarget ? dropTarget.zone : null;
                    return (
                      <div
                        key={s.id}
                        draggable={dndEnabled}
                        onDragStart={(e) => {
                          e.dataTransfer.effectAllowed = "move";
                          setDragId(s.id);
                          invalidRef.current = new Set([
                            s.id,
                            ...descIds(flatStaff, s.id),
                          ]);
                        }}
                        onDragOver={(e) => {
                          if (!dragId || invalidRef.current.has(s.id)) return;
                          e.preventDefault();
                          setDropTarget({ id: s.id, zone: zoneFor(e) });
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (dragId && isTarget) applyMove(dragId, s.id, zone);
                          clearDrag();
                        }}
                        onDragEnd={clearDrag}
                        className={[
                          "relative flex items-center gap-2 py-2.5 pr-3 transition-colors",
                          dndEnabled ? "cursor-grab active:cursor-grabbing" : "",
                          dragId === s.id ? "opacity-40" : "",
                          zone === "child" ? "bg-violet-50 ring-1 ring-inset ring-violet-300" : "",
                        ].join(" ")}
                        style={{ paddingLeft: 12 + (staffQuery ? 0 : s.depth * 24) }}
                        data-testid={`hub-staff-row-${s.id}`}
                      >
                        {zone === "before" && (
                          <span className="pointer-events-none absolute inset-x-2 top-0 flex items-center">
                            <span className="h-2 w-2 rounded-full border-2 border-violet-500 bg-white" />
                            <span className="h-0.5 flex-1 rounded bg-violet-500" />
                          </span>
                        )}
                        {zone === "after" && (
                          <span className="pointer-events-none absolute inset-x-2 bottom-0 flex items-center">
                            <span className="h-2 w-2 rounded-full border-2 border-violet-500 bg-white" />
                            <span className="h-0.5 flex-1 rounded bg-violet-500" />
                          </span>
                        )}
                        {zone === "child" && (
                          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-violet-600 px-2 py-0.5 text-[10px] font-semibold text-white">
                            Alt görevli
                          </span>
                        )}
                        {dndEnabled && (
                          <GripVertical
                            className="h-4 w-4 shrink-0 text-zinc-300"
                            data-testid={`hub-staff-drag-${s.id}`}
                          />
                        )}
                        {!staffQuery && s.depth > 0 && (
                          <CornerDownRight className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-sm font-medium text-zinc-900">
                              {s.name}
                            </span>
                            {s.role && (
                              <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700">
                                {s.role}
                              </span>
                            )}
                          </div>
                          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-zinc-500">
                            {s.phone && (
                              <a
                                href={`tel:${s.phone.replace(/[^+\d]/g, "")}`}
                                onClick={(e) => e.stopPropagation()}
                                className="flex items-center gap-1 hover:text-violet-700 hover:underline"
                                title="Ara"
                                data-testid={`hub-staff-phone-link-${s.id}`}
                              >
                                <Phone className="h-3 w-3" /> {s.phone}
                              </a>
                            )}
                            {s.email && (
                              <a
                                href={`mailto:${s.email}`}
                                onClick={(e) => e.stopPropagation()}
                                className="flex items-center gap-1 hover:text-violet-700 hover:underline"
                                title="E-posta gönder"
                                data-testid={`hub-staff-email-link-${s.id}`}
                              >
                                <Mail className="h-3 w-3" /> {s.email}
                              </a>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => editStaff(s)}
                          data-testid={`hub-staff-edit-${s.id}`}
                          title="Düzenle"
                          className="flex h-7 w-7 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        {!staffQuery && s.depth > 0 && (
                          <button
                            type="button"
                            onClick={() => promote(s)}
                            data-testid={`hub-staff-promote-${s.id}`}
                            title="Bir üst seviyeye taşı"
                            className="flex h-7 w-7 items-center justify-center rounded-md text-zinc-400 hover:bg-violet-50 hover:text-violet-700"
                          >
                            <CornerUpLeft className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {confirmStaffDelete === s.id ? (
                          <button
                            type="button"
                            onClick={() => removeStaff(s.id)}
                            data-testid={`hub-staff-delete-confirm-${s.id}`}
                            className="flex h-7 items-center rounded-md bg-red-50 px-2 text-[11px] font-semibold text-red-600 hover:bg-red-100"
                          >
                            Sil?
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setConfirmStaffDelete(s.id)}
                            data-testid={`hub-staff-delete-${s.id}`}
                            title="Sil"
                            className="flex h-7 w-7 items-center justify-center rounded-md text-zinc-400 hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : null}

              {dragId && (
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDropTarget({ id: "__root__", zone: "root" });
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    applyMove(dragId, "__root__", "root");
                    clearDrag();
                  }}
                  data-testid="hub-staff-root-dropzone"
                  className={[
                    "mb-3 rounded-lg border-2 border-dashed py-3 text-center text-xs font-medium transition-colors",
                    dropTarget?.id === "__root__"
                      ? "border-violet-500 bg-violet-50 text-violet-700"
                      : "border-zinc-300 text-zinc-400",
                  ].join(" ")}
                >
                  Ana seviyeye taşımak için buraya bırakın
                </div>
              )}

              <form
                onSubmit={submitStaff}
                className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-3"
              >
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-zinc-600">
                    <UserPlus className="h-3.5 w-3.5" />
                    {editingStaffId ? "Görevliyi düzenle" : "Görevli ekle"}
                  </div>
                  {editingStaffId && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditingStaffId(null);
                        setStaffForm(EMPTY_STAFF);
                      }}
                      data-testid="hub-staff-cancel-edit"
                      className="flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-800"
                    >
                      <X className="h-3 w-3" /> Vazgeç
                    </button>
                  )}
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <div>
                    <Label className="text-[11px] text-zinc-500">Görev</Label>
                    <Input
                      value={staffForm.role}
                      onChange={(e) =>
                        setStaffForm((f) => ({ ...f, role: e.target.value }))
                      }
                      placeholder="örn. Saha Sorumlusu"
                      list="hub-role-suggestions"
                      className="mt-0.5 h-9 bg-white"
                      maxLength={80}
                      data-testid="hub-staff-role-input"
                    />
                    <datalist id="hub-role-suggestions">
                      {(detail.role_suggestions || []).map((r) => (
                        <option key={r} value={r} />
                      ))}
                    </datalist>
                  </div>
                  <div>
                    <Label className="text-[11px] text-zinc-500">Ad Soyad *</Label>
                    <Input
                      value={staffForm.name}
                      onChange={(e) =>
                        setStaffForm((f) => ({ ...f, name: e.target.value }))
                      }
                      placeholder="Ad Soyad"
                      className="mt-0.5 h-9 bg-white"
                      maxLength={120}
                      data-testid="hub-staff-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] text-zinc-500">Cep Telefonu</Label>
                    <Input
                      value={staffForm.phone}
                      onChange={(e) =>
                        setStaffForm((f) => ({ ...f, phone: e.target.value }))
                      }
                      placeholder="+90 5xx xxx xx xx"
                      className="mt-0.5 h-9 bg-white"
                      maxLength={40}
                      data-testid="hub-staff-phone-input"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] text-zinc-500">E-posta</Label>
                    <Input
                      type="email"
                      value={staffForm.email}
                      onChange={(e) =>
                        setStaffForm((f) => ({ ...f, email: e.target.value }))
                      }
                      placeholder="ornek@firma.com"
                      className="mt-0.5 h-9 bg-white"
                      maxLength={120}
                      data-testid="hub-staff-email-input"
                    />
                  </div>
                </div>
                <div className="mt-2 flex justify-end">
                  <Button
                    type="submit"
                    size="sm"
                    disabled={savingStaff || !staffForm.name.trim()}
                    data-testid="hub-staff-save-btn"
                    className="bg-zinc-900 text-white hover:bg-zinc-800"
                  >
                    {editingStaffId ? "Güncelle" : "Ekle"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
